import React, { useState, useEffect } from 'react';
import { Bot, Power, Edit2, RefreshCw, Database, Info, X, Check, Search, BarChart3, MessageSquare, Send } from 'lucide-react';
import axios from 'axios';
import { postKnowledge } from './kbClient';

interface VirtualAgent {
  id: string;
  name: string;
  nickname: string;
  description: string;
  skill: string;
  prompt: string;
  platformPrompt: string;
  contentPath: string;
  isEnabled: boolean;
  isPublished: boolean;
  difyAppId?: string;
  difyApiKey?: string;
  lastSyncedAt?: string;
}

export const VirtualizationTab: React.FC<{ 
  tenantId?: string, 
  readOnly?: boolean 
}> = ({ tenantId, readOnly = false }) => {
  // Decide API base path: if tenantId is provided, we are in Backoffice mode
  const apiBase = tenantId ? `/api/backoffice` : '/api';
  const listUrl = tenantId ? `${apiBase}/tenants/${tenantId}/virtual-agents` : `${apiBase}/virtual-agents`;

  const [agents, setAgents] = useState<VirtualAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingAgent, setEditingAgent] = useState<VirtualAgent | null>(null);
  const [syncingId, setSyncingAt] = useState<string | null>(null);
  const [testMessage, setTestMessage] = useState('');
  const [chatHistory, setChatHistory] = useState<{role: 'user' | 'assistant', content: string}[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [scorecardAgent, setScorecardAgent] = useState<VirtualAgent | null>(null);
  const [metrics, setMetrics] = useState<any>(null);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  // D2 (AC3): KB-paste control state (tenant mode).
  const [kbText, setKbText] = useState('');
  const [kbStatus, setKbStatus] = useState<'idle' | 'working' | 'done' | 'error'>('idle');
  const [kbMsg, setKbMsg] = useState<string | null>(null);

  const handleAddKnowledge = async () => {
    if (!editingAgent || !kbText.trim()) return;
    setKbStatus('working'); setKbMsg(null);
    const authHeader = tenantId ? backofficeAuth : undefined;
    const r = await postKnowledge(apiBase, editingAgent.id, kbText, authHeader);
    if (r.ok) {
      setKbStatus('done'); setKbText('');
    } else {
      setKbStatus('error'); setKbMsg(r.error || 'Could not add knowledge.');
    }
  };

  // SECURITY: backoffice cred removed from the client bundle. The backoffice UI now authorizes via
  // the operator's authenticated SESSION (platform superadmin) — see the dual-path in server/backoffice.ts.
  // Same-origin requests carry the session cookie, so no Authorization header is needed.
  const backofficeAuth: string | undefined = undefined;

  const fetchMetrics = async (agent: VirtualAgent) => {
    console.log(`[VirtualizationTab] fetchMetrics called for agent:`, agent.id);
    try {
      setLoadingMetrics(true);
      setScorecardAgent(agent);
      const headers: any = {};
      if (tenantId) headers['Authorization'] = backofficeAuth;
      
      const url = `${apiBase}/virtual-agents/${agent.id}/metrics`;
      console.log(`[VirtualizationTab] Fetching from URL: ${url}`);
      const res = await axios.get(url, { headers });
      console.log(`[VirtualizationTab] Metrics received:`, res.data);
      setMetrics(res.data);
    } catch (err) {
      console.error('[VirtualizationTab] Failed to fetch metrics', err);
      alert('Failed to load metrics. Check console for details.');
    } finally {
      setLoadingMetrics(false);
    }
  };
  
  useEffect(() => {
    fetchAgents();
  }, [tenantId]);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      console.log(`[VirtualizationTab] Fetching agents...`);
      const headers: any = {};
      if (tenantId) headers['Authorization'] = backofficeAuth;

      const response = await axios.get(listUrl, { headers });
      
      console.log('[VirtualizationTab] Response type:', typeof response.data);
      if (Array.isArray(response.data)) {
        setAgents(response.data);
      } else {
        setAgents([]);
      }
    } catch (err: any) {
      console.error('[VirtualizationTab] Failed to fetch virtual agents', err);
      setAgents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (agent: VirtualAgent) => {
    if (readOnly) return;
    try {
      const updated = { ...agent, isEnabled: !agent.isEnabled };
      const headers: any = {};
      if (tenantId) headers['Authorization'] = backofficeAuth;

      await axios.patch(`${apiBase}/virtual-agents/${agent.id}`, updated, { headers });
      setAgents(agents.map(a => a.id === agent.id ? updated : a));
    } catch (err) {
      alert('Failed to toggle agent');
    }
  };

  const handleSync = async (agentId: string) => {
    if (readOnly) return;
    try {
      setSyncingAt(agentId);
      const headers: any = {};
      if (tenantId) headers['Authorization'] = backofficeAuth;

      await axios.post(`${apiBase}/virtual-agents/${agentId}/sync`, {}, { headers });
      alert('Sync triggered successfully');
    } catch (err) {
      alert('Sync failed');
    } finally {
      setSyncingAt(null);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingAgent || readOnly) return;
    try {
      const headers: any = {};
      if (tenantId) headers['Authorization'] = backofficeAuth;

      // If we are publishing this agent, locally unpublish others first for UI consistency
      let updatedAgents = [...agents];
      if (editingAgent.isPublished) {
        updatedAgents = agents.map(a => ({
          ...a,
          isPublished: a.id === editingAgent.id ? true : false
        }));
      } else {
        updatedAgents = agents.map(a => a.id === editingAgent.id ? editingAgent : a);
      }

      await axios.patch(`${apiBase}/virtual-agents/${editingAgent.id}`, editingAgent, { headers });
      setAgents(updatedAgents);
      setEditingAgent(null);
    } catch (err) {
      alert('Update failed');
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!testMessage.trim() || !editingAgent || isSending) return;

    const userMsg = testMessage;
    setTestMessage('');
    setChatHistory(prev => [...prev, { role: 'user', content: userMsg }]);
    setIsSending(true);

    try {
      const headers: any = {
        'Content-Type': 'application/json'
      };
      if (tenantId) headers['Authorization'] = backofficeAuth;
      
      console.log(`[VirtualizationTab] Sending test-chat request to ${apiBase}/virtual-agents/${editingAgent.id}/test-chat`);
      
      const response = await fetch(`${apiBase}/virtual-agents/${editingAgent.id}/test-chat`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ message: userMsg, history: chatHistory })
      });

      if (!response.body) throw new Error('No response body');
      
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantAnswer = '';

      setChatHistory(prev => [...prev, { role: 'assistant', content: '' }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        const text = decoder.decode(value, { stream: true });
        const lines = text.split('\n');
        
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.chunk) {
              assistantAnswer += data.chunk;
              setChatHistory(prev => {
                const newHistory = [...prev];
                newHistory[newHistory.length - 1].content = assistantAnswer;
                return newHistory;
              });
            }
            if (data.done) {
              assistantAnswer = data.answer;
              setChatHistory(prev => {
                const newHistory = [...prev];
                newHistory[newHistory.length - 1].content = assistantAnswer;
                return newHistory;
              });
            }
          } catch (err) { /* ignore partial json */ }
        }
      }
    } catch (err: any) {
      console.error('[VirtualizationTab] Chat error:', err);
      const errorMsg = err.message || "Failed to get response from agent.";
      setChatHistory(prev => [...prev, { role: 'assistant', content: `Error: ${errorMsg}` }]);
    } finally {
      setIsSending(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-text-muted">Loading virtual agents...</div>;

  return (
    <div className="space-y-6">
      {/* List Table */}
      <div className="bg-[#1e293b] rounded-xl border border-[#334155] overflow-hidden shadow-sm">
        <div className="p-6 border-b border-[#334155] flex justify-between items-center bg-slate-nav/50">
          <div>
            <h3 className="text-lg font-semibold text-white">Configured Auto-Agents</h3>
            <p className="text-sm text-text-muted">Every tenant has 4 specialized dimensions</p>
          </div>
          <div className="px-3 py-1 bg-blue-900/30 text-blue-400 border border-blue-800/50 rounded-full text-xs font-bold flex items-center gap-2">
            <Database size={12} /> Local Gemma 2B Active
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-slate-nav text-text-muted font-medium">
              <tr>
                <th className="px-6 py-3">Agent Persona</th>
                <th className="px-6 py-3">Skill</th>
                <th className="px-6 py-3">Grounding Path</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#334155]">
              {agents?.map((agent) => (
                <tr key={agent.id} className="hover:bg-[#2a3848] transition-colors group">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3 cursor-pointer" onClick={() => setEditingAgent(agent)}>
                      <div className={`p-2 rounded-lg ${agent.isEnabled ? 'bg-blue-900/30 text-blue-400' : 'bg-gray-800 text-text-muted'}`}>
                        <Bot size={20} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <div className="font-bold text-white uppercase tracking-wider text-xs underline decoration-blue-500/30 hover:decoration-blue-500 transition-all">{agent.nickname}</div>
                          {agent.isPublished && (
                            <span className="text-[10px] bg-primary text-white px-1.5 py-0.5 rounded font-black animate-pulse">PUBLISHED</span>
                          )}
                        </div>
                        <div className="text-text-muted text-xs">{agent.name}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-xs font-semibold px-2 py-1 rounded bg-slate-nav text-gray-300 border border-[#334155]">
                      {agent.skill}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-text-muted font-mono text-xs italic">
                    {agent.contentPath || 'No path set'}
                  </td>
                  <td className="px-6 py-4">
                    <button 
                      onClick={() => handleToggle(agent)}
                      disabled={readOnly}
                      className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold transition-all ${
                        agent.isEnabled 
                        ? 'bg-green-900/30 text-green-400 border border-green-800/50' 
                        : 'bg-red-900/20 text-red-500/70 border border-red-900/30'
                      } ${readOnly ? 'cursor-default' : 'hover:scale-105'}`}
                    >
                      <Power size={12} /> {agent.isEnabled ? 'ENABLED' : 'DISABLED'}
                    </button>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button 
                        onClick={() => setEditingAgent(agent)}
                        className="p-2 text-text-muted hover:text-white hover:bg-[#334155] rounded-lg transition-colors"
                        title={readOnly ? "View Configuration" : "Edit Persona"}
                      >
                        <Edit2 size={16} />
                      </button>
                      <button 
                        onClick={() => fetchMetrics(agent)}
                        className="p-2 text-text-muted hover:text-blue-400 hover:bg-[#334155] rounded-lg transition-colors"
                        title="Agent Scorecard / Metrics"
                      >
                        <BarChart3 size={16} />
                      </button>
                      {!readOnly && (
                        <button 
                          disabled={syncingId === agent.id}
                          onClick={() => handleSync(agent.id)}
                          className={`p-2 text-text-muted hover:text-blue-400 hover:bg-[#334155] rounded-lg transition-colors ${syncingId === agent.id ? 'animate-spin text-blue-400' : ''}`}
                          title="Sync Knowledge Base"
                        >
                          <RefreshCw size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Modal */}
      {editingAgent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className={`bg-[#1e293b] w-full ${!readOnly ? 'max-w-6xl' : 'max-w-2xl'} rounded-2xl border border-[#334155] shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200 flex flex-col max-h-[90vh]`}>
            
            <div className="p-6 border-b border-[#334155] flex justify-between items-center bg-slate-nav/50">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Bot className="text-blue-400" /> {readOnly ? 'View' : 'Configure'} {editingAgent.skill} Agent
              </h3>
              <button type="button" onClick={() => { setEditingAgent(null); setChatHistory([]); }} className="text-text-muted hover:text-white"><X size={24} /></button>
            </div>

            <div className="flex-1 flex overflow-hidden">
              {/* Config Form */}
              <div className={`flex-1 p-6 space-y-4 overflow-y-auto border-r border-[#334155]/50`}>
                <form onSubmit={handleUpdate} id="agent-config-form">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-text-muted uppercase mb-1">Display Name</label>
                      <input 
                        disabled={readOnly}
                        className="w-full bg-slate-nav border border-[#334155] rounded-lg p-2.5 text-white focus:ring-2 focus:ring-blue-500 outline-none disabled:opacity-50"
                        value={editingAgent.name}
                        onChange={e => setEditingAgent({...editingAgent, name: e.target.value})}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-text-muted uppercase mb-1">Nickname (ID)</label>
                      <input 
                        disabled={readOnly}
                        className="w-full bg-slate-nav border border-[#334155] rounded-lg p-2.5 text-white focus:ring-2 focus:ring-blue-500 outline-none disabled:opacity-50"
                        value={editingAgent.nickname}
                        onChange={e => setEditingAgent({...editingAgent, nickname: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-4">
                    <div className="col-span-2">
                      <label className="block text-xs font-bold text-text-muted uppercase mb-1">Description</label>
                      <input
                        disabled={readOnly}
                        className="w-full bg-slate-nav border border-[#334155] rounded-lg p-2.5 text-white focus:ring-2 focus:ring-blue-500 outline-none disabled:opacity-50"
                        value={editingAgent.description}
                        onChange={e => setEditingAgent({...editingAgent, description: e.target.value})}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-text-muted uppercase mb-1">Skill</label>
                      <select
                        data-testid="agent-skill-select"
                        disabled={readOnly}
                        className="w-full bg-slate-nav border border-[#334155] rounded-lg p-2.5 text-white focus:ring-2 focus:ring-blue-500 outline-none disabled:opacity-50"
                        value={editingAgent.skill}
                        onChange={e => setEditingAgent({...editingAgent, skill: e.target.value})}
                      >
                        <option value="SUPPORT">SUPPORT</option>
                        <option value="SALES">SALES</option>
                        <option value="CUSTOMER_SUCCESS">CUSTOMER_SUCCESS</option>
                        <option value="CUSTOM">CUSTOM</option>
                      </select>
                    </div>
                  </div>

                  {!readOnly && (
                    <div className="mt-4 p-4 bg-blue-900/10 border border-blue-800/30 rounded-xl flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${editingAgent.isPublished ? 'bg-primary text-white' : 'bg-gray-800 text-text-muted'}`}>
                          <RefreshCw size={20} className={editingAgent.isPublished ? 'animate-spin-slow' : ''} />
                        </div>
                        <div>
                          <div className="text-sm font-bold text-white">Publish to Live Website</div>
                          <div className="text-xs text-text-muted">When enabled, this agent will automatically take and answer all incoming chats for your tenant.</div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setEditingAgent({...editingAgent, isPublished: !editingAgent.isPublished})}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                          editingAgent.isPublished ? 'bg-primary' : 'bg-gray-700'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            editingAgent.isPublished ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  )}

                  {/* D1 (AC3): platform-managed internal prompt — backoffice/support only.
                      "PLATFORM MANAGED" jargon is noise to a self-serve tenant. tenantId set = backoffice. */}
                  {tenantId && (
                  <div className="mt-4">
                    <label className="block text-xs font-bold text-text-muted uppercase mb-1 flex justify-between">
                      <span>Platform-level Prompt (Biombo Standard)</span>
                      <span className="text-[10px] text-blue-400 bg-blue-900/20 px-1.5 rounded">PLATFORM MANAGED</span>
                    </label>
                    <textarea
                      disabled={true}
                      rows={3}
                      className="w-full bg-slate-nav/50 border border-[#334155] rounded-lg p-2.5 text-text-muted focus:ring-2 focus:ring-blue-500 outline-none font-mono text-xs italic"
                      value={editingAgent.platformPrompt || 'System instructions from Biombo platform...'}
                    />
                  </div>
                  )}

                  <div className="mt-4">
                    <label className="block text-xs font-bold text-text-muted uppercase mb-1">Tenant Persona Prompt (Your Customizations)</label>
                    <textarea 
                      disabled={readOnly}
                      rows={6}
                      className="w-full bg-slate-nav border border-[#334155] rounded-lg p-2.5 text-white focus:ring-2 focus:ring-blue-500 outline-none font-mono text-sm placeholder:text-text-muted"
                      placeholder="Enter specific instructions for this agent's personality and goals..."
                      value={editingAgent.prompt}
                      onChange={e => setEditingAgent({...editingAgent, prompt: e.target.value})}
                    />
                  </div>
                  {/* D1 (AC3): Dify App ID / API Key are DEAD (Dify retired from inference in C2) and the
                      raw server-path Content Path is not stranger-usable (superseded by the KB paste-add,
                      D2). Show them ONLY in backoffice (tenantId set) for support/debug; a self-serve
                      tenant never sees dead/internal fields it can't act on. */}
                  {tenantId && (
                  <>
                  <div className="mt-4 grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-text-muted uppercase mb-1">Dify App ID</label>
                      <input
                        disabled={readOnly}
                        placeholder="App ID from Dify Console"
                        className="w-full bg-slate-nav border border-[#334155] rounded-lg p-2.5 text-white focus:ring-2 focus:ring-blue-500 outline-none disabled:opacity-50"
                        value={editingAgent.difyAppId || ''}
                        onChange={e => setEditingAgent({...editingAgent, difyAppId: e.target.value})}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-text-muted uppercase mb-1">Dify API Key</label>
                      <input
                        disabled={readOnly}
                        type="password"
                        placeholder="App API Key (app-xxxx)"
                        className="w-full bg-slate-nav border border-[#334155] rounded-lg p-2.5 text-white focus:ring-2 focus:ring-blue-500 outline-none disabled:opacity-50"
                        value={editingAgent.difyApiKey || ''}
                        onChange={e => setEditingAgent({...editingAgent, difyApiKey: e.target.value})}
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="block text-xs font-bold text-text-muted uppercase mb-1">Knowledge Content Path</label>
                    <div className="flex gap-2">
                      <input
                        disabled={readOnly}
                        placeholder="/home/tzuvy/knowledge/..."
                        className="flex-1 bg-slate-nav border border-[#334155] rounded-lg p-2.5 text-white focus:ring-2 focus:ring-blue-500 outline-none disabled:opacity-50"
                        value={editingAgent.contentPath || ''}
                        onChange={e => setEditingAgent({...editingAgent, contentPath: e.target.value})}
                      />
                      {!readOnly && <button type="button" className="px-4 py-2 bg-[#334155] text-white rounded-lg hover:bg-[#475569] transition-colors">Browse</button>}
                    </div>
                  </div>
                  </>
                  )}
                  {/* D2 (AC3): stranger-usable KB add — TENANT mode only (backoffice keeps the raw
                      contentPath field above). Paste → POST /kb (the ONLY contentPath writer). */}
                  {!tenantId && (
                  <div className="mt-4">
                    <label className="block text-xs font-bold text-text-muted uppercase mb-1">Knowledge (paste anything your agent should know)</label>
                    <textarea
                      rows={5}
                      className="w-full bg-slate-nav border border-[#334155] rounded-lg p-2.5 text-white focus:ring-2 focus:ring-blue-500 outline-none text-sm resize-none"
                      placeholder="e.g. Our coffee beans are $18/bag. We offer free returns within 30 days…"
                      value={kbText}
                      onChange={e => setKbText(e.target.value)}
                    />
                    <div className="flex items-center gap-3 mt-2">
                      <button
                        type="button"
                        disabled={kbStatus === 'working' || !kbText.trim()}
                        onClick={handleAddKnowledge}
                        className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary transition-colors disabled:opacity-50"
                      >
                        {kbStatus === 'working' ? 'Adding…' : 'Add Knowledge'}
                      </button>
                      {kbStatus === 'done' && <span className="text-xs text-green-400">Added — your agent will use it.</span>}
                      {kbStatus === 'error' && <span className="text-xs text-red-400">{kbMsg}</span>}
                    </div>
                  </div>
                  )}
                </form>
              </div>

              {/* Chat Tester (Only in Tenant Settings mode) */}
              {!readOnly && (
                <div className="w-[400px] bg-slate-nav/30 flex flex-col border-l border-[#334155]/50">
                  <div className="p-4 border-b border-[#334155]/50 flex items-center justify-between">
                    <h4 className="text-sm font-bold text-white flex items-center gap-2">
                      <MessageSquare size={16} className="text-blue-400" /> Test Chat Interface
                    </h4>
                    <button 
                      onClick={() => setChatHistory([])}
                      className="text-[10px] text-text-muted hover:text-gray-300 font-bold uppercase"
                    >
                      Clear History
                    </button>
                  </div>
                  
                  <div className="flex-1 overflow-y-auto p-4 space-y-4 font-sans text-sm">
                    {chatHistory.length === 0 ? (
                      <div className="h-full flex flex-col items-center justify-center text-center opacity-40">
                        <Bot size={40} className="mb-2" />
                        <p className="text-xs">Send a message to test how your agent responds with current configuration.</p>
                      </div>
                    ) : (
                      chatHistory.map((msg, i) => (
                        <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[85%] p-3 rounded-2xl ${
                            msg.role === 'user' 
                            ? 'bg-primary text-white rounded-tr-none' 
                            : 'bg-[#334155] text-gray-200 rounded-tl-none'
                          }`}>
                            {msg.content}
                          </div>
                        </div>
                      ))
                    )}
                    {isSending && (
                      <div className="flex justify-start">
                        <div className="bg-[#334155] p-3 rounded-2xl rounded-tl-none flex gap-1">
                          <span className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce"></span>
                          <span className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce delay-75"></span>
                          <span className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce delay-150"></span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="p-4 bg-slate-nav/50 border-t border-[#334155]/50">
                    <form onSubmit={handleSendMessage} className="flex gap-2">
                      <input 
                        className="flex-1 bg-[#1e293b] border border-[#334155] rounded-lg px-3 py-2 text-white text-sm outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="Type a message..."
                        value={testMessage}
                        onChange={e => setTestMessage(e.target.value)}
                        disabled={isSending}
                      />
                      <button 
                        type="submit"
                        disabled={isSending || !testMessage.trim()}
                        className="p-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 transition-colors"
                      >
                        <Send size={18} />
                      </button>
                    </form>
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-[#334155] flex justify-end gap-3 bg-slate-nav/30">
              <button 
                type="button" 
                onClick={() => { setEditingAgent(null); setChatHistory([]); }} 
                className="px-6 py-2 text-text-muted font-bold hover:text-white transition-colors"
              >
                {readOnly ? 'CLOSE' : 'CANCEL'}
              </button>
              {!readOnly && (
                <button 
                  form="agent-config-form"
                  type="submit" 
                  className="px-8 py-2 bg-primary text-white font-bold rounded-lg hover:bg-primary-dark transition-all shadow-lg shadow-blue-900/40 flex items-center gap-2"
                >
                  <Check size={18} /> SAVE PERSONA
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Scorecard Modal */}
      {scorecardAgent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-[#1e293b] w-full max-w-lg rounded-2xl border border-[#334155] shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-6 border-b border-[#334155] flex justify-between items-center bg-slate-nav/50">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <BarChart3 className="text-blue-400" /> {scorecardAgent.name} Scorecard
              </h3>
              <button type="button" onClick={() => setScorecardAgent(null)} className="text-text-muted hover:text-white"><X size={24} /></button>
            </div>
            
            <div className="p-8">
              {loadingMetrics ? (
                <div className="py-12 text-center text-text-muted animate-pulse">Calculating performance...</div>
              ) : metrics ? (
                <div className="space-y-8">
                  {/* Big Score */}
                  <div className="text-center">
                    <div className="text-5xl font-black text-white mb-1">{metrics.performanceScore}%</div>
                    <div className="text-xs font-bold text-text-muted uppercase tracking-widest">Performance Index</div>
                  </div>

                  {/* Grid Stats */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-nav p-4 rounded-xl border border-[#334155]">
                      <div className="text-2xl font-bold text-white">{metrics.totalChats}</div>
                      <div className="text-[10px] font-bold text-text-muted uppercase">Chats Handled</div>
                    </div>
                    <div className="bg-slate-nav p-4 rounded-xl border border-[#334155]">
                      <div className="text-2xl font-bold text-white">{metrics.avgResponseTime}s</div>
                      <div className="text-[10px] font-bold text-text-muted uppercase">Avg Response Time</div>
                    </div>
                    <div className="bg-slate-nav p-4 rounded-xl border border-[#334155]">
                      <div className="text-2xl font-bold text-red-400">{metrics.escalations}</div>
                      <div className="text-[10px] font-bold text-text-muted uppercase">Escalations</div>
                    </div>
                    <div className="bg-slate-nav p-4 rounded-xl border border-[#334155]">
                      <div className="text-2xl font-bold text-green-400">{metrics.resolved}</div>
                      <div className="text-[10px] font-bold text-text-muted uppercase">Resolved</div>
                    </div>
                  </div>

                  {/* Specific Goals */}
                  <div className="pt-4 border-t border-[#334155]/50">
                    <div className="flex justify-between items-end mb-2">
                      <div className="text-xs font-bold text-text-muted uppercase">
                        {scorecardAgent.skill === 'SALES' ? 'Sales Goals Achieved' : 'Customer Satisfaction'}
                      </div>
                      <div className="text-sm font-bold text-blue-400">{metrics.goalsMet} / {metrics.totalChats}</div>
                    </div>
                    <div className="w-full bg-slate-nav h-2 rounded-full overflow-hidden">
                      <div 
                        className="bg-primary h-full transition-all duration-1000" 
                        style={{ width: `${metrics.performanceScore}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center text-red-400">Failed to load metrics.</div>
              )}
            </div>

            <div className="p-6 border-t border-[#334155] flex justify-center bg-slate-nav/30">
              <button 
                onClick={() => setScorecardAgent(null)}
                className="w-full py-3 bg-[#334155] text-white font-bold rounded-xl hover:bg-[#475569] transition-colors uppercase tracking-widest text-xs"
              >
                Close Dashboard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
